import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";
import { Separator } from "@/components/ui/separator";
import { AddUsersComponent } from "@/components/users/add-users.component";
import { useAccountSnapshot } from "@/stores/account.store";
import { useUserStore } from "@/stores/user.store";
import { NewUserPayload, User, UserSchema } from "@/types/user.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { debounce } from "lodash";
import { ArrowUpDown, EditIcon, TrashIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const UsersPage = () => {
  const accountSnapshot = useAccountSnapshot();
  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "username",
      header: "Username"
    },
    {
      accessorKey: "firstName",
      header: "First Name"
    },
    {
      accessorKey: "familyName",
      header: "Family Name"
    },
    {
      accessorKey: "email",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Email
            <ArrowUpDown className="w-4 h-4 ml-2" />
          </Button>
        );
      }
    },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) => {
        return row.original.email == accountSnapshot.userInfo?.profile.email ? (
          <></>
        ) : (
          <DialogTrigger
            onClick={() => {
              setCurrentSelectedUser(row.original);
              setModalType("delete");
              setOpenDeleteUserDialog(true);
            }}
          >
            <TrashIcon color="red" />
          </DialogTrigger>
        );
      }
    },
    {
      accessorKey: "edit",
      header: "",
      cell: ({ row }) => {
        return row.original.email == accountSnapshot.userInfo?.profile.email ? (
          <></>
        ) : (
          <DialogTrigger
            onClick={() => {
              setModalType("edit");
              setCurrentSelectedUser(row.original);
              setOpenUpdateUserDialog(true);
              updateUserForm.setValue("email", row.original.email);
              updateUserForm.setValue("familyName", row.original.familyName);
              updateUserForm.setValue("firstName", row.original.firstName);
              updateUserForm.setValue("username", row.original.username);
            }}
          >
            <EditIcon></EditIcon>
          </DialogTrigger>
        );
      }
    }
  ];
  const { userSnapshot, userProxy } = useUserStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSelectedUser, setCurrentSelectedUser] = useState<
    User | undefined
  >(undefined);
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
  const [openUpdateUserDialog, setOpenUpdateUserDialog] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "delete">("edit");

  useEffect(() => {
    userProxy.fetchUsers();
  }, []);

  const handlePageChange = (newOffset: number) => {
    userProxy.fetchUsers(userSnapshot.limit, newOffset, searchQuery);
  };

  const handleSearchChange = debounce((query: string) => {
    setSearchQuery(query);
    userProxy.fetchUsers(userSnapshot.limit, 0, query);
  }, 500);

  const onSubmitDeleteUser = () => {
    if (currentSelectedUser) {
      userProxy.deleteUser(currentSelectedUser.id);
      setOpenDeleteUserDialog(false);
    } else {
      toast.error("An error has occured : user id is empty");
    }
  };

  const onSubmitUpdateUser = () => {
    if (!currentSelectedUser?.id) {
      toast.error("An error has occured : user id is empty");
    } else {
      userProxy.updateUser(updateUserForm.getValues(), currentSelectedUser.id);
      setOpenUpdateUserDialog(false);
    }
  };

  const updateUserForm = useForm<NewUserPayload>({
    resolver: zodResolver(UserSchema),
    defaultValues: {
      email: "",
      familyName: "",
      firstName: "",
      username: ""
    }
  });

  return (
    <div className="p-6 pb-16 space-y-6">
      <div className="px-0">
        <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        <p className="text-muted-foreground">
          Manage user accounts in your organization
        </p>
      </div>
      <Separator />
      <div className="flex justify-between w-full px-0">
        <Input
          placeholder="Search users..."
          onChange={e => handleSearchChange(e.target.value)}
          className="max-w-sm"
        />
        <AddUsersComponent />
      </div>
      <CardContent className="px-0 p-0">
        <Dialog
          open={
            modalType == "edit" ? openUpdateUserDialog : openDeleteUserDialog
          }
          onOpenChange={
            modalType == "edit"
              ? setOpenUpdateUserDialog
              : setOpenDeleteUserDialog
          }
        >
          <DataTable
            columns={columns}
            data={[...userSnapshot.users]}
            isLoading={userSnapshot.isLoading}
          />
          <DialogContent>
            {modalType == "edit" ? (
              <DialogHeader>
                <DialogTitle>Update User</DialogTitle>
                <DialogDescription>
                  Update the user data that you want
                </DialogDescription>
                <div>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Update an user</DialogTitle>
                      <DialogDescription>
                        Fill in the informations about the user.
                      </DialogDescription>
                    </DialogHeader>
                    <Form {...updateUserForm}>
                      <form
                        className="w-full space-y-4"
                        onSubmit={updateUserForm.handleSubmit(
                          onSubmitUpdateUser
                        )}
                      >
                        <div className="grid grid-cols-2 gap-4">
                          <div className="grid gap-2">
                            <FormField
                              control={updateUserForm.control}
                              name="firstName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>First Name</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      required
                                      placeholder="Jack"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="grid gap-2">
                            <FormField
                              control={updateUserForm.control}
                              name="familyName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Family Name</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      required
                                      placeholder="Roberts"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="grid gap-2">
                            <FormField
                              control={updateUserForm.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Email</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      required
                                      placeholder="jack.roberts@mycompany.com"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="grid gap-2">
                            <FormField
                              control={updateUserForm.control}
                              name="username"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Username</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      required
                                      placeholder="jack.roberts"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 pt-5">
                          <Button
                            type="submit"
                            className="w-full"
                            onClick={() => {
                              updateUserForm.formState.isValid;
                            }}
                          >
                            Update the user
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </DialogContent>
                </div>
              </DialogHeader>
            ) : (
              <DialogHeader>
                <DialogTitle>
                  Delete user {currentSelectedUser?.email} ?
                </DialogTitle>
                <DialogDescription>
                  Please write confirm to confirm the deletion
                </DialogDescription>
                <div className="grid grid-cols-1 gap-4">
                  <div className="grid grid-cols-1 gap-4 pt-3">
                    <Button
                      type="submit"
                      className="w-full"
                      variant="destructive"
                      onClick={() => onSubmitDeleteUser()}
                    >
                      Delete the user
                    </Button>
                  </div>
                </div>
              </DialogHeader>
            )}
          </DialogContent>
        </Dialog>
        <div className="flex justify-center mt-4">
          <Pagination>
            <PaginationPrevious
              aria-disabled={userSnapshot.offset <= 0}
              className={
                userSnapshot.offset <= 0
                  ? "pointer-events-none opacity-50"
                  : "hover:cursor-pointer"
              }
              onClick={() =>
                handlePageChange(userSnapshot.offset - userSnapshot.limit)
              }
            />
            <PaginationNext
              aria-disabled={userSnapshot.users.length < userSnapshot.limit}
              className={
                userSnapshot.users.length < userSnapshot.limit
                  ? "pointer-events-none opacity-50"
                  : "hover:cursor-pointer"
              }
              onClick={() =>
                handlePageChange(userSnapshot.offset + userSnapshot.limit)
              }
            />
          </Pagination>
        </div>
      </CardContent>
    </div>
  );
};

export default UsersPage;
